import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, OnInit, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellEditEndingEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { QuillViewComponent } from 'ngx-quill'
import { Subscription, firstValueFrom } from 'rxjs'
import { MeetingEntityTypes } from 'src/app/shared/services/meeting-hub.service'
import { getInitials } from 'src/app/shared/shared-functions'

import {
    BaseComponent,
    FeatureType,
    Grid,
    Headline,
    LEADERSHIP_L10_MEETING_ID,
    ListItem,
    Meeting,
    ModalBackDrop,
    ModalEditMode,
    ModalSize,
    ToDo,
    User,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { MeetingItemsSelectionComponent } from '../meeting-items-selection/meeting-items-selection.component'
import { ToDoComponent } from '../to-dos/to-do.component'
import { HeadlineComponent } from './headline.component'

@Component({
    selector: 'app-headlines',
    templateUrl: './headlines.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        QuillViewComponent,
    ],
})
export class HeadlinesComponent extends BaseComponent implements OnInit, OnDestroy {
    faSearch = faSearch

    meetingId = input.required<string>()
    meetings = input.required<Meeting[]>()
    meeting = input.required<Meeting>()
    isHeadline = input.required<boolean>()
    isActiveMeeting = input(false)

    allUsers: User[] = []
    allUserMap!: DataMap
    allActiveUsers: User[] = []
    allActiveUserList: ListItem<string>[] = []

    canViewAdmin = true

    grid!: Grid<Headline>

    isRankingMode = false
    selectedForRanking: Headline[] = []

    private contentSyncSub?: Subscription
    private isCellEditing = false
    private pendingContentSync = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingEdit])
    }

    get isLeadershipMeeting() {
        return this.meetingId()?.toLowerCase() === LEADERSHIP_L10_MEETING_ID.toLowerCase()
    }

    get unresolvedOpportunities(): Headline[] {
        return this.grid.items.filter(item => !item.doneDate)
    }

    get canRankItems(): boolean {
        return this.isActiveMeeting() && !this.isHeadline() && this.unresolvedOpportunities.length >= 2
    }

    get maxRankingSelection(): number {
        const unresolvedCount = this.unresolvedOpportunities.length
        return unresolvedCount >= 3 ? 3 : unresolvedCount
    }

    constructor() {
        super()

        effect(() => {
            if (this.meetingId()) {
                this.refreshHeadlines()
            }
        })
    }

    ngOnInit(): void {
        this.grid = new Grid<Headline>(this.services)
            .setupModalEditing(
                this.services.data.headlines,
                this.isHeadline() ? 'Headline' : 'Opportunity',
                HeadlineComponent,
                () => new Headline(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.name])
            .doInitialSelection(false)

        this.contentSyncSub = this.services.meetingContentSync.subscribe({
            meetingId: () => this.meetingId(),
            entityTypes: [MeetingEntityTypes.Headline],
            isEditing: () => this.isCellEditing || this.isRankingMode,
            onRefresh: () => this.refreshHeadlines(true),
            onDeferredRefresh: () => {
                this.pendingContentSync = true
            },
        })
    }

    ngOnDestroy(): void {
        this.contentSyncSub?.unsubscribe()
    }

    refreshHeadlines(silent = false) {
        const params = new HttpParams().set('meetingId', this.meetingId()).set('headlineType', this.isHeadline() ? 0 : 1)
        this.cancelableRequest(
            'HeadlinesRefresh',
            !silent,
            this.services.data
                .load(
                    !silent,
                    !silent,
                    this.services.data.headlines.queryAllByAction('GetByMeetingId', params),
                    this.services.data.users.queryAllByAction('GetForLookup'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.allUsers = result[1]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'displayName')

                        this.allActiveUsers = this.allUsers.filter(user => user.active)
                        this.allActiveUserList = this.allActiveUsers.map(user => new ListItem(user.id, user.displayName))
                        if (this.pendingContentSync && !this.isCellEditing && !this.isRankingMode) {
                            this.pendingContentSync = false
                        }
                    },
                }),
        )
    }

    beginningEdit(): void {
        this.isCellEditing = true
    }

    cellEditEnded(_flexGrid: FlexGrid, args: CellEditEndingEventArgs): void {
        this.isCellEditing = false
        if (args.cancel) return
        if (this.pendingContentSync && !this.isRankingMode) {
            this.pendingContentSync = false
            this.refreshHeadlines(true)
        }
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as Headline
            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = getInitials(originalVal)
            }

            cell.style.textDecoration = 'unset'
            if (dataItem && dataItem.doneDate != null && c > 0) {
                cell.style.textDecoration = 'line-through'
            }
        }
    }

    showSelectionModal() {
        const ref = this.services.modals.open(MeetingItemsSelectionComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as MeetingItemsSelectionComponent
        instance.itemType = this.isHeadline() ? 'Headlines' : 'Opportunities'
        instance.meetingId = this.meetingId()
        instance.itemIds = this.grid.items.map(x => x.id)
        ref.result.then(
            () => {
                this.refreshHeadlines()
            },
            () => {
                console.log('dismissed')
            },
        )
    }

    solveOpportunity(headline: Headline) {
        if (headline.doneDate) {
            headline.doneDate = null
        } else {
            headline.doneDate = new Date()
            // Remove from ranking selection if it was selected
            if (this.isRankingMode) {
                const index = this.selectedForRanking.findIndex(item => item.id === headline.id)
                if (index >= 0) {
                    this.selectedForRanking.splice(index, 1)
                }
            }
        }
        this.services.data.headlines.edit(headline).subscribe({
            next: () => {
                this.grid.refresh()
            },
            error: error => {
                this.services.notification.error('Failed to mark opportunity as solved: ' + error.message)
            },
        })
    }

    addToDoForOpportunity() {
        if (this.isHeadline()) {
            return
        }

        const opportunity = this.grid.selectedItem
        if (!opportunity) {
            this.services.notification.warning('Please select an opportunity to convert to a To-Do.')
            return
        }

        const newToDo = new ToDo()
        newToDo.name = opportunity.name
        newToDo.details = opportunity.details

        const modalEditOptions = {
            target: newToDo,
            initializeParams: this,
            mode: ModalEditMode.adding,
            repository: this.services.data.toDos,
            typeName: 'To-Do',
        }

        const ref = this.services.modals.open(ToDoComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const repoModal: ToDoComponent = ref.componentInstance
        if (repoModal.initialize) {
            repoModal.initialize(modalEditOptions)
        }
        // Carry the opportunity's assignee onto the new to-do.
        repoModal.preselectedUserId = opportunity.userId

        ref.result.then(
            () => {
                // The opportunity has been turned into a to-do, so mark the issue solved.
                opportunity.doneDate = new Date()
                this.services.data.headlines.edit(opportunity).subscribe({
                    next: () => {
                        this.services.notification.success('To-Do created and issue marked solved')
                        this.refreshHeadlines()
                    },
                    error: error => {
                        this.services.notification.error('To-Do created, but the issue could not be marked solved: ' + error.message)
                    },
                })
            },
            () => {
                // User cancelled
            },
        )
    }

    toggleRankingMode() {
        this.isRankingMode = !this.isRankingMode
        if (!this.isRankingMode) {
            this.selectedForRanking = []
        }
        this.grid.refresh()
    }

    isSelectedForRanking(headline: Headline): boolean {
        return this.selectedForRanking.some(item => item.id === headline.id)
    }

    toggleRankingSelection(headline: Headline) {
        if (headline.doneDate) {
            return // Don't allow selecting resolved items
        }

        const index = this.selectedForRanking.findIndex(item => item.id === headline.id)
        if (index >= 0) {
            this.selectedForRanking.splice(index, 1)
        } else {
            const unresolvedCount = this.unresolvedOpportunities.length
            const maxSelection = unresolvedCount >= 3 ? 3 : unresolvedCount

            if (this.selectedForRanking.length < maxSelection) {
                this.selectedForRanking.push(headline)

                // Auto-apply if we've reached max selection (3 items, or 2 items if only 2 unresolved)
                if (this.selectedForRanking.length === maxSelection) {
                    // Use setTimeout to ensure the UI updates before applying
                    setTimeout(() => {
                        this.applyRanks()
                    }, 0)
                    return
                }
            } else {
                this.services.notification.warning(`You can only select up to ${maxSelection} opportunities for ranking`)
            }
        }
        this.grid.refresh()
    }

    applyRanks() {
        const selectedCount = this.selectedForRanking.length
        const unresolvedCount = this.unresolvedOpportunities.length
        const maxSelection = unresolvedCount >= 3 ? 3 : unresolvedCount

        // Allow applying if we have the maximum number of items (3 if 3+ unresolved, or all if 2 unresolved)
        if (selectedCount === 0) {
            return
        }

        if (selectedCount !== maxSelection) {
            this.services.notification.warning(
                `Please select exactly ${maxSelection} ${maxSelection === 1 ? 'opportunity' : 'opportunities'} to rank`,
            )
            return
        }

        // Get IDs of selected items for comparison
        const selectedIds = new Set(this.selectedForRanking.map(item => item.id))

        // Assign ranks 1, 2, 3 to selected items (or 1, 2 if only 2 items, or 1 if only 1 item)
        this.selectedForRanking.forEach((headline, index) => {
            headline.rank = index + 1
        })

        // Find all other unresolved opportunities and set them to rank 4
        const remainingUnresolved = this.unresolvedOpportunities.filter(item => !selectedIds.has(item.id))
        remainingUnresolved.forEach(headline => {
            headline.rank = 4
        })

        // Save all items (selected + remaining unresolved)
        const allItemsToSave = [...this.selectedForRanking, ...remainingUnresolved]
        const savePromises = allItemsToSave.map(headline => firstValueFrom(this.services.data.headlines.edit(headline)))

        Promise.all(savePromises)
            .then(() => {
                this.services.notification.success('Ranks applied successfully')
                this.selectedForRanking = []
                this.isRankingMode = false
                this.grid.refresh()
            })
            .catch(error => {
                this.services.notification.error('Failed to apply ranks: ' + error.message)
            })
    }

    cancelRanking() {
        this.selectedForRanking = []
        this.isRankingMode = false
        this.grid.refresh()
    }
}
